import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Upload } from "lucide-react";
import { photoSrc } from "@/components/funnel/SuccessStories";

const EMPTY = { name: "", role: "", company: "", course: "", quote: "", photo_url: "", published: true };

const StoryForm = ({ initial, onSaved, onClose, t }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/stories/photo", fd);
      setF((x) => ({ ...x, photo_url: data.photo_url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("funnel.common.error"));
    } finally {
      setUploading(false);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, role: f.role, company: f.company, course: f.course, quote: f.quote, photo_url: f.photo_url, published: f.published };
      if (initial?.story_id) await api.put(`/admin/stories/${initial.story_id}`, body);
      else await api.post("/admin/stories", body);
      toast.success(t("admin.storySaved"));
      onSaved();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("admin.storyInvalid"));
    } finally {
      setBusy(false);
    }
  };
  const field = (k, label, props = {}) => (
    <div><Label htmlFor={`s-${k}`}>{label}</Label><Input id={`s-${k}`} value={f[k]} onChange={set(k)} className="mt-1" data-testid={`story-input-${k}`} {...props} /></div>
  );
  return (
    <form onSubmit={save} className="space-y-3" data-testid="story-form">
      <div className="flex items-center gap-3">
        {f.photo_url ? <img src={photoSrc(f.photo_url)} alt="" className="h-16 w-16 rounded-full object-cover" /> : <span className="h-16 w-16 rounded-full bg-slate-100" />}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="story-photo-input" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="story-photo-upload">
          {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} {t("admin.storyPhoto")}
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("name", t("admin.storyName"), { required: true })}
        {field("role", t("admin.storyRole"), { required: true })}
        {field("company", t("admin.storyCompany"), { required: true })}
        {field("course", t("admin.storyCourse"))}
      </div>
      <div><Label htmlFor="s-quote">{t("admin.storyQuote")}</Label><Textarea id="s-quote" rows={3} required minLength={10} maxLength={400} value={f.quote} onChange={set("quote")} className="mt-1" data-testid="story-input-quote" /></div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="story-published" /> {t("admin.storyPublished")}</label>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>{t("funnel.common.back")}</Button>
        <Button type="submit" disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="story-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {t("admin.counterSave")}</Button>
      </div>
    </form>
  );
};

export const StoriesManager = () => {
  const { t } = useTranslation();
  const [stories, setStories] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/stories").then(({ data }) => setStories(data)).catch(() => setStories([]));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm(t("admin.storyDeleteConfirm"))) return;
    await api.delete(`/admin/stories/${id}`).then(() => { toast.success(t("admin.storyDeleted")); load(); }).catch(() => toast.error(t("funnel.common.error")));
  };
  return (
    <div className="space-y-4" data-testid="stories-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{t("admin.storiesHelp")}</p>
        <Button onClick={() => setEditing({})} className="bg-amber-600 hover:bg-amber-700" data-testid="story-add"><Plus className="mr-1.5 h-4 w-4" /> {t("admin.storyAdd")}</Button>
      </div>
      {stories === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-amber-600" />}
      <div className="grid gap-3 md:grid-cols-2">
        {stories?.map((s) => (
          <Card key={s.story_id} className="flex gap-4 border-slate-200 p-4" data-testid="admin-story-row">
            {s.photo_url ? <img src={photoSrc(s.photo_url)} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" /> : <span className="h-14 w-14 shrink-0 rounded-full bg-slate-100" />}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-bold text-slate-900">{s.name}</p>
                {s.sample && <Badge variant="outline" data-testid="admin-story-sample">{t("stories.sample")}</Badge>}
                {!s.published && <Badge className="bg-slate-200 text-slate-700">{t("admin.storyHidden")}</Badge>}
              </div>
              <p className="text-sm text-emerald-700">{s.role} · {s.company}</p>
              <p className="mt-1 line-clamp-2 text-sm text-slate-600">{s.quote}</p>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="icon" variant="ghost" onClick={() => setEditing(s)} data-testid="story-edit"><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(s.story_id)} data-testid="story-delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.story_id ? t("admin.storyEdit") : t("admin.storyAdd")}</DialogTitle></DialogHeader>
          {editing && <StoryForm initial={editing.story_id ? editing : null} t={t} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
